import React, { useState, useMemo, useEffect } from 'react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  LoadingBlock,
} from '../../../../shared/ui/composites';
import { formatReportValue, styleClassNames } from '../../utils/reportFormat';
import { displayLabel } from '../../utils/columnLabels';
import { exportTableExcel } from '../../utils/exportTableExcel';
import { resolveTablePageSize } from '../../utils/widgetPreviewData';
import { cn } from '../../../../lib/utils';
import {
  resolveColumnValue,
  resolveRowStyle,
  resolveCellStyle,
  formatColumnExport,
  pickEmoji,
  starCount,
  resolveTableColumnLayout,
  resolveTableLook,
  resolveColumnTextClass,
  isMeterDisplay,
} from '../../utils/tableColumnDisplay';
import MeterCell, { rowMeterStyle } from './MeterCell';

function CellDisplay({ col, value, row, tableLook }) {
  const display = col.display || 'text';
  if (isMeterDisplay(display)) {
    return <MeterCell col={col} value={value} row={row} tableLook={tableLook} />;
  }
  if (display === 'emoji') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-base leading-none">{pickEmoji(value, col)}</span>
        <span className="text-[10px] text-slate-500 tabular-nums">{formatReportValue(value, col)}</span>
      </span>
    );
  }
  if (display === 'stars') {
    const n = starCount(value, col);
    return (
      <span className="inline-flex items-center gap-0.5 text-amber-500 tracking-tight" title={formatReportValue(value, col)}>
        {'★'.repeat(n)}
        <span className="text-slate-300">{'☆'.repeat(5 - n)}</span>
      </span>
    );
  }
  return formatReportValue(value, col);
}

export default function TableWidget({ widget, data, loading, error, compact = false }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { tableConfig = {} } = widget;
  const pageSize = resolveTablePageSize(widget);

  const cols = useMemo(() => {
    const columnDefs = (tableConfig.columns?.length > 0)
      ? tableConfig.columns
      : (data?.columns || []);
    return columnDefs.map((c) =>
      typeof c === 'string'
        ? { key: c, label: displayLabel(c), format: 'text' }
        : { ...c, label: displayLabel(c) },
    );
  }, [tableConfig.columns, data?.columns]);

  const columnLayout = useMemo(
    () => resolveTableColumnLayout(cols, tableConfig),
    [cols, tableConfig],
  );
  const tableLook = useMemo(() => resolveTableLook(tableConfig), [tableConfig]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    let result = [...data.rows];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter((row) => {
        if (Object.values(row).some((v) => String(v).toLowerCase().includes(q))) return true;
        return cols.some((col) => String(formatColumnExport(row, col)).toLowerCase().includes(q));
      });
    }

    if (sortKey) {
      const col = cols.find((c) => c.key === sortKey);
      result.sort((a, b) => {
        const av = col ? resolveColumnValue(a, col) : a[sortKey];
        const bv = col ? resolveColumnValue(b, col) : b[sortKey];
        const na = Number(av);
        const nb = Number(bv);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) {
          return sortDir === 'asc' ? na - nb : nb - na;
        }
        return sortDir === 'asc'
          ? String(av ?? '').localeCompare(String(bv ?? ''))
          : String(bv ?? '').localeCompare(String(av ?? ''));
      });
    }

    return result;
  }, [data, search, sortKey, sortDir, cols]);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleExcelExport = () => {
    exportTableExcel({
      filename: widget.title || 'table',
      columns: cols,
      rows,
    });
  };

  if (loading) return <LoadingBlock />;
  if (error) return <div className="text-sm text-red-500 p-2">{error}</div>;

  return (
    <div className="flex flex-col h-full gap-2">
      {!compact && (
      <div className="flex items-center gap-2 shrink-0">
        <input
          type="text"
          placeholder="جستجو..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          dir="rtl"
        />
        <button
          type="button"
          onClick={handleExcelExport}
          disabled={!rows.length}
          title="خروجی اکسل همه ردیف‌ها (نه فقط صفحه فعلی)"
          className="shrink-0 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-40"
        >
          اکسل ({rows.length})
        </button>
      </div>
      )}
      <div className="flex-1 overflow-auto">
        <Table fit={columnLayout.fit} className={columnLayout.tableClass}>
          <TableHeader>
            <TableRow>
              {cols.map((col, colIdx) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'cursor-pointer select-none',
                    columnLayout.truncate ? 'truncate' : 'whitespace-nowrap',
                    tableLook.headerTextClass,
                    tableLook.headerItalic && 'italic',
                    tableLook.compact ? '!h-8 !px-2 !py-1' : '!h-10 !px-3 !py-2',
                    styleClassNames({
                      align: col.headerAlign || tableLook.headerAlign,
                      bold: tableLook.headerBold,
                    }),
                  )}
                  dir={col.dir || undefined}
                  style={{
                    color: tableLook.headerColor || undefined,
                    backgroundColor: tableLook.headerBg || undefined,
                    ...columnLayout.styles[colIdx],
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span className="mr-1 text-indigo-500">
                      {sortDir === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols.length} className="text-center text-slate-400 py-6">
                  داده‌ای موجود نیست
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row, i) => {
                const rowLook = resolveRowStyle(row, cols);
                return (
                  <TableRow
                    key={i}
                    className={rowLook.className}
                    style={{ ...rowLook.style, ...rowMeterStyle(rowLook.meter) }}
                  >
                    {cols.map((col, colIdx) => {
                      const value = resolveColumnValue(row, col);
                      const cell = resolveCellStyle(col, value, row);
                      const meter = isMeterDisplay(col.display);
                      return (
                        <TableCell
                          key={col.key}
                          className={styleClassNames(col, cn(
                            cell.className,
                            resolveColumnTextClass(col, tableLook),
                            tableLook.compact ? '!p-1.5' : '!px-3 !py-2',
                            meter && '!overflow-visible align-middle',
                            columnLayout.truncate && !meter && 'truncate',
                            columnLayout.fit && !meter && 'max-w-0',
                            columnLayout.nowrap && !meter && 'whitespace-nowrap',
                          ))}
                          dir={cell.dir}
                          style={{ ...cell.style, ...columnLayout.styles[colIdx] }}
                        >
                          <CellDisplay col={col} value={value} row={row} tableLook={tableLook} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-2 text-sm text-slate-600 shrink-0">
          <button
            type="button"
            className="rounded px-3 py-1 border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
          >
            قبلی
          </button>
          <span>
            صفحه {safePage + 1} از {pageCount}
            <span className="text-slate-400"> — {rows.length} ردیف · {pageSize} در صفحه</span>
          </span>
          <button
            type="button"
            className="rounded px-3 py-1 border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={safePage >= pageCount - 1}
          >
            بعدی
          </button>
        </div>
      )}
    </div>
  );
}
